'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import styles from './HeroAtmosphere.module.css'

/**
 * The hero atmosphere: footage as texture, never as subject. The re-cut loop
 * runs behind the presentation stage at low opacity, blurred, desaturated and
 * tinted by the January garnet, masked so it dissolves into the near-black
 * field. The poster frame is server-rendered `<picture>` markup with the same
 * treatment, so it is the complete atmosphere — and the LCP candidate — for
 * no-JS, reduced-motion, Save-Data and phone visitors, and for the first paint
 * everywhere else. The `<video>` only mounts after hydration and only when it
 * is affordable: motion allowed, no Save-Data, and a viewport wide enough for
 * the loop to read as atmosphere rather than as the whole screen.
 *
 * The layer is decorative and `aria-hidden`. The one interactive element, the
 * pause control, lives outside it, reports its state through `aria-pressed`,
 * and remembers the visitor's choice for the session.
 */

export type HeroAtmosphereMedia = {
  readonly mp4: string
  readonly webm: string
  readonly poster: {
    readonly avif: string
    readonly webp: string
    readonly src: string
    readonly width: number
    readonly height: number
  }
}

type Props = {
  readonly media: HeroAtmosphereMedia
}

/** Below this the loop would be the screen, not a texture behind the object. */
const MIN_VIDEO_VIEWPORT = 600
const STORAGE_KEY = 'mist.hero-atmosphere'

function readPausedPreference(): boolean {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === 'paused'
  } catch {
    return false
  }
}

function writePausedPreference(paused: boolean) {
  try {
    if (paused) window.sessionStorage.setItem(STORAGE_KEY, 'paused')
    else window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage is a convenience, never a requirement.
  }
}

function motionAffordable(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false
  } catch {
    return false
  }
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  if (connection?.saveData === true) return false
  return window.innerWidth >= MIN_VIDEO_VIEWPORT
}

export default function HeroAtmosphere({ media }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [videoOk, setVideoOk] = useState(false)
  const [paused, setPaused] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [inView, setInView] = useState(true)

  // Decided once after mount; a change in the reduced-motion preference
  // mid-session retires the loop for good rather than restarting it.
  useEffect(() => {
    if (!motionAffordable()) return
    setPaused(readPausedPreference())
    setVideoOk(true)
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => {
      if (query.matches) setVideoOk(false)
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  // An off-screen or background loop is pure cost: it only decodes while the
  // hero is actually on a foregrounded screen.
  useEffect(() => {
    if (!videoOk) return
    const video = videoRef.current
    if (!video || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting)), { rootMargin: '80px' })
    observer.observe(video)
    const onVisibility = () => setInView(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [videoOk])

  const play = useCallback(async () => {
    const video = videoRef.current
    if (!video) return
    try {
      // React sets `muted` as a property, not an attribute; autoplay policy
      // reads the property, but pin it before play() to be certain.
      video.muted = true
      const result: unknown = video.play()
      if (result && typeof (result as Promise<void>).then === 'function') await (result as Promise<void>)
      setPlaying(true)
    } catch {
      // Autoplay refused or the source failed: the poster is the atmosphere.
      setPlaying(false)
    }
  }, [])

  useEffect(() => {
    if (!videoOk) return
    const video = videoRef.current
    if (!video) return
    if (paused || !inView) {
      try {
        video.pause()
      } catch {
        // Nothing to pause.
      }
      if (paused) setPlaying(false)
      return
    }
    void play()
  }, [videoOk, paused, inView, play])

  const toggle = () => {
    setPaused((current) => {
      const next = !current
      writePausedPreference(next)
      return next
    })
  }

  return (
    <>
      <div className={styles.layer} aria-hidden="true" data-hero-atmosphere={videoOk ? (playing ? 'playing' : 'poster') : 'still'}>
        <div className={styles.field}>
          <picture className={styles.poster}>
            <source srcSet={media.poster.avif} type="image/avif" />
            <source srcSet={media.poster.webp} type="image/webp" />
            <img
              src={media.poster.src}
              alt=""
              width={media.poster.width}
              height={media.poster.height}
              decoding="async"
              fetchPriority="high"
            />
          </picture>
          {videoOk ? (
            <video
              ref={videoRef}
              className={styles.video}
              data-playing={playing && !paused ? 'true' : undefined}
              muted
              loop
              playsInline
              preload="metadata"
              poster={media.poster.src}
              disablePictureInPicture
              disableRemotePlayback
              tabIndex={-1}
              onError={() => setPlaying(false)}
            >
              <source src={media.webm} type="video/webm" />
              <source src={media.mp4} type="video/mp4" />
            </video>
          ) : null}
          <span className={styles.tint} />
        </div>
      </div>
      {videoOk ? (
        <button
          type="button"
          className={styles.control}
          aria-pressed={paused}
          aria-label={paused ? 'Play atmosphere' : 'Pause atmosphere'}
          onClick={toggle}
        >
          <svg viewBox="0 0 12 12" width="10" height="10" focusable="false" aria-hidden="true">
            {paused ? <path d="M3 1.5v9l7-4.5z" fill="currentColor" /> : <path d="M3 1.5h2v9H3zM7 1.5h2v9H7z" fill="currentColor" />}
          </svg>
        </button>
      ) : null}
    </>
  )
}
